import { useState } from 'react'
import { router } from '@inertiajs/react'

import { Alert, Button, LoadingSpinner } from '@atoms'
import { Card, CardHeading } from '@components/imports/Card'
import { CsvBoxNotSetUpAlert } from '@components/imports/CsvBoxNotSetUpAlert'
import { inlineLinkClasses } from '@components/imports/ImportSummary'
import { SupporterImportCsvBox } from '@components/imports/SupporterImportCsvBox'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { type CsvBoxColumn, type CsvBoxEnvironment } from '@utils/imports/types'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('supporter_imports')

export type DraftImport = { id: number; token: string }

export type SupporterImporterProps = {
  requestingUserId: number
  draftImport: DraftImport
  licenseKey: string | null
  csvBoxEnvironment: CsvBoxEnvironment
  columns: CsvBoxColumn[]
  groupName: string
  templatePath: string
  createImportPath: string
}

function ImportTypeCard({ groupName }: { groupName: string }) {
  return (
    <Card>
      <CardHeading icon="file-05" title={t('import_type.title')} />
      <dl className="tw:m-0 tw:flex tw:flex-col tw:gap-1 tw:p-5 tw:text-sm">
        <dt className="tw:font-medium tw:text-gray-cool-900">{t('import_type.group_members')}</dt>
        <dd className="tw:m-0 tw:text-gray-cool-600">
          {t('import_type.group_members_description', { group: groupName })}
        </dd>
      </dl>
    </Card>
  )
}

function TemplateLink({ templatePath }: { templatePath: string }) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2 tw:border-t tw:border-gray-100 tw:px-5 tw:py-4 tw:text-sm">
      <span className="tw:text-gray-cool-600">{t('template.prompt')}</span>
      <a href={templatePath} className={cn(inlineLinkClasses, 'tw:flex tw:items-center tw:gap-1')}>
        <Icon type="file-download-03" className="tw:h-4 tw:w-4" aria-hidden />
        {t('template.download')}
      </a>
    </div>
  )
}

function UploadCard({
  requestingUserId,
  draftImport,
  licenseKey,
  csvBoxEnvironment,
  columns,
  groupName,
  templatePath,
  createImportPath,
}: SupporterImporterProps) {
  const [rejected, setRejected] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const finishImport = (accepted: boolean) => {
    setRejected(!accepted)
    if (!accepted) return

    router.post(
      createImportPath,
      { import_id: draftImport.id },
      { onStart: () => setSubmitting(true), onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <Card>
      <CardHeading icon="upload-cloud-02" title={t('upload.title')} subtitle={t('upload.subtitle')} />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        {rejected && (
          <Alert type="error" title={t('upload.rejected_title')}>
            {t('upload.rejected_body')}
          </Alert>
        )}
        {licenseKey ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-xl tw:border-2 tw:border-dashed tw:border-primary-200 tw:bg-primary-50/40 tw:px-6 tw:py-10 tw:text-center">
            <span
              aria-hidden
              className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:text-primary-600 tw:shadow-xs"
            >
              <Icon type="upload-cloud-02" className="tw:h-6 tw:w-6" />
            </span>
            <p className="tw:mb-0 tw:text-sm tw:font-medium tw:text-gray-cool-900">{t('upload.prompt')}</p>
            <p className="tw:mb-0 tw:max-w-md tw:text-xs tw:text-gray-cool-600">
              {t('upload.hint', { group: groupName })}
            </p>
            {submitting ? (
              <Button type="button" size="xl" disabled>
                <LoadingSpinner size="sm" />
                {t('upload.submitting')}
              </Button>
            ) : (
              <SupporterImportCsvBox
                requestingUserId={requestingUserId}
                importToken={draftImport.token}
                licenseKey={licenseKey}
                environment={csvBoxEnvironment}
                columns={columns}
                onImport={finishImport}
              />
            )}
          </div>
        ) : (
          <CsvBoxNotSetUpAlert />
        )}
      </div>
      <TemplateLink templatePath={templatePath} />
    </Card>
  )
}

function ChecksCard({ scope, keys }: { scope: 'before_submit' | 'after_submit'; keys: string[] }) {
  return (
    <Card>
      <CardHeading icon="check-done-01" title={t(`${scope}.title`)} />
      <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5 tw:text-sm">
        {keys.map((key) => (
          <li key={key} className="tw:flex tw:items-start tw:gap-2.5 tw:py-3 tw:text-gray-cool-700">
            <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" aria-hidden />
            {t(`${scope}.${key}`)}
          </li>
        ))}
      </ul>
    </Card>
  )
}

export function SupporterImporter(props: SupporterImporterProps) {
  return (
    <div className="tw:flex tw:max-w-4xl tw:flex-col tw:gap-5">
      <ImportTypeCard groupName={props.groupName} />
      <UploadCard {...props} />
      <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
        <ChecksCard scope="before_submit" keys={['match_columns', 'required_columns', 'fix_issues']} />
        <ChecksCard scope="after_submit" keys={['background', 'summary', 'failures']} />
      </div>
    </div>
  )
}
